import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useEffect, useState, useRef } from 'react';
import { Alert, Stack } from '@mui/material';

/**
 *
 * @param headers is a list of header names (strings)
 * @param rows is a list of AvlTableCell objects lists.
 *
 * type AvlTableCell {
 *      align: string,
 *      style: {},
 *      data: {},
 * }
 * @param pageCount is a total count of pages
 */
export function ReorderableTable(
    {
        headers,
        rows,
        onReorder,
    },
) {
    const [tableWidth, setTableWidth] = useState(0);
    const tableRef = useRef(null);
    // Refs for FLIP animation
    const rowRefs = useRef(new Map()); // key -> DOM element

    // Helper to generate a stable key for each row
    const getRowKey = (row, fallbackIndex) => {
        // Prefer explicit keys if present on row
        if (row?.key !== undefined && row?.key !== null) return row.key;
        if (row?.id !== undefined && row?.id !== null) return row.id;
        // Fallback to a derived key based on cell data
        try {
            if (Array.isArray(row?.cells)) {
                const signature = row.cells.map((c) => String(c?.data ?? '')).join('||');
                if (signature) return signature;
            }
        } catch (e) {
            // noop
        }
        // Last resort: index (note: reorders will remount if only index is used)
        return `idx-${fallbackIndex}`;
    };

    // Add effect to measure and update table width
    useEffect(() => {
        const updateTableWidth = () => {
            if (tableRef.current) {
                setTableWidth(tableRef.current.offsetWidth);
            }
        };

        updateTableWidth();
        window.addEventListener('resize', updateTableWidth);

        return () => {
            window.removeEventListener('resize', updateTableWidth);
        };
    }, []);

    const row = (row, rowIndex) => {
        const key = getRowKey(row, rowIndex);
            return (
                <>
                    <TableRow
                        key={key}
                        ref={(el) => {
                            if (el) {
                                rowRefs.current.set(key, el);
                            } else {
                                rowRefs.current.delete(key);
                            }
                        }}
                    >
                        {row.cells.map((cell, cellIndex) => (
                            <TableCell
                                key={`${key}:${cellIndex}`}
                                align={cell.align}
                                style={cell.style}
                            >
                                <div
                                    style={{
                                        minHeight: '42px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        flexDirection: 'column',
                                        whiteSpace: 'pre-line',
                                    }}
                                >
                                    {cell.data}
                                </div>
                            </TableCell>
                        ))}
                    </TableRow>
                </>
            );
    };

    return !rows.length
        ? (
            <Stack sx={{ width: '100%' }} spacing={2}>
                <Alert severity="warning">Records not found!</Alert>
            </Stack>
        ) : (
            <TableContainer>
                <Table ref={tableRef}>
                    <TableHead>
                        <TableRow>
                            {headers.map((header, headerIndex) => (
                                <TableCell
                                    key={headerIndex}
                                    ref={header.ref}
                                    align={header.align}
                                >
                                    {header.label}
                                </TableCell>
                            ))}
                            <TableCell align='center'>
                                Action
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((data, rowIndex) => row(data, rowIndex))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
}